/**
 * Override queue + override-as-diff surface (§5.5 PLATFORM-SPEC).
 *
 * Lists human_gated / abstain-routed items awaiting a human decision and
 * presents each as a before/after diff (presented_as=diff). Decisions go
 * through the ONE shared handler (`./review-decision` → taskActions.setStatus
 * → override.human) with wall_ms measured from presentation → click.
 *
 * Also lists recorded OverrideRecords as a real field-level diff of what the
 * human changed against what the system proposed — not a free-text note.
 *
 * Extends Decision / Card / DataTable / StatusBadge only — no second approve
 * path, no bespoke modal.
 */

import * as React from "react";
import {
  Card,
  CardContent,
  Decision,
  DataTable,
  StatusBadge,
  formatRelativeTime,
  type DataTableColumn,
} from "@excellent/ui";
import { apiKey, useApi } from "@excellent/ui/api";
import { useSWRConfig } from "swr";
import {
  overrides,
  type OverrideQueueItem,
  type OverrideRecordItem,
} from "@excellent/ui/api/contracts/overrides";
import {
  decideTaskReview,
  promptDecisionReason,
} from "./review-decision";

/** Track wall_ms from first paint of the queue (presentation) → decision. */
function usePresentedAtMs(): number {
  const ref = React.useRef<number | null>(null);
  if (ref.current == null) ref.current = Date.now();
  return ref.current;
}

function DiffPane({
  label,
  value,
}: {
  label: string;
  value: { status: string; human_gated: number; title: string };
}) {
  return (
    <div className="rounded-md border border-border/40 bg-muted/30 p-2 type-subtext">
      <p className="font-medium text-muted-foreground">{label}</p>
      <dl className="mt-1 grid grid-cols-[auto_1fr] gap-x-2 gap-y-0.5">
        <dt className="text-muted-foreground">status</dt>
        <dd className="font-mono">{value.status}</dd>
        <dt className="text-muted-foreground">human_gated</dt>
        <dd className="font-mono">{value.human_gated}</dd>
        <dt className="text-muted-foreground">title</dt>
        <dd className="truncate">{value.title}</dd>
      </dl>
    </div>
  );
}

function OverrideDiffEvidence({ item }: { item: OverrideQueueItem }) {
  const afterReject = {
    status: "in_progress" as const,
    human_gated: 0,
    title: item.title,
  };
  return (
    <div className="flex flex-col gap-2">
      {item.strandedDone ? (
        <p className="type-subtext text-amber-700 dark:text-amber-400">
          Stranded done+gated (#44753 class) — accept clears the gate without
          re-running ship; reject bounces to in_progress.
        </p>
      ) : null}
      {item.reasons.length > 0 ? (
        <p className="type-subtext text-muted-foreground">
          Escalation: {item.reasons.join("; ")}
        </p>
      ) : null}
      {item.descriptionExcerpt ? (
        <p className="line-clamp-3 type-subtext text-muted-foreground">
          {item.descriptionExcerpt}
        </p>
      ) : null}
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        <DiffPane label="Before (current)" value={item.before} />
        <DiffPane label="After on accept" value={item.afterAccept} />
      </div>
      <p className="type-subtext text-muted-foreground">
        Reject → status {afterReject.status}, human_gated {afterReject.human_gated}
      </p>
    </div>
  );
}

function OverrideRowDecision({
  item,
  presentedAtMs,
  busy,
  onBusy,
  onDone,
}: {
  item: OverrideQueueItem;
  presentedAtMs: number;
  busy: boolean;
  onBusy: (v: boolean) => void;
  onDone: () => void;
}) {
  const act = async (decision: "accept" | "reject", reason?: string) => {
    onBusy(true);
    try {
      const wallMs = Math.max(0, Date.now() - presentedAtMs);
      await decideTaskReview({
        taskId: item.taskId,
        projectSlug: item.projectSlug,
        sectionSlug: item.sectionSlug,
        decision,
        reason,
        instrument: {
          presentedAs: "diff",
          wallMs,
          presentedAtMs,
        },
      });
      onDone();
    } finally {
      onBusy(false);
    }
  };

  return (
    <Decision
      className="w-full"
      prompt={
        item.strandedDone
          ? "Clear the human gate on this already-done task?"
          : "Accept or reject this escalated outcome?"
      }
      evidence={<OverrideDiffEvidence item={item} />}
      acceptLabel={item.strandedDone ? "Clear gate" : "Accept"}
      rejectLabel="Reject"
      onAccept={() => void act("accept")}
      onReject={() => {
        const reason = promptDecisionReason("reject");
        if (reason === undefined) return;
        void act("reject", reason);
      }}
      busy={busy}
      ownerLabel={item.projectName}
      verifierLabel="you"
    />
  );
}

export function OverrideQueue() {
  const presentedAtMs = usePresentedAtMs();
  const { mutate } = useSWRConfig();
  const { data, error, isLoading } = useApi(overrides.list, {
    query: { limit: "100" },
  });
  const [busyId, setBusyId] = React.useState<number | null>(null);
  const [selectedId, setSelectedId] = React.useState<number | null>(null);
  const [msg, setMsg] = React.useState<string | null>(null);

  const items = data?.items ?? [];
  const selected =
    items.find((i) => i.taskId === selectedId) ?? items[0] ?? null;

  const refresh = () => {
    void mutate(apiKey(overrides.list, { query: { limit: "100" } }));
  };

  const columns: DataTableColumn<OverrideQueueItem>[] = [
    {
      key: "task",
      header: "Task",
      render: (r) => (
        <div className="min-w-0">
          <div className="truncate font-medium">
            #{r.taskId} {r.title}
          </div>
          <div className="truncate type-subtext text-muted-foreground">
            {r.projectName} · {r.sectionName}
          </div>
        </div>
      ),
    },
    {
      key: "status",
      header: "Status",
      width: "w-28",
      render: (r) => (
        <StatusBadge
          domain="task"
          status={
            (r.status === "done" ||
            r.status === "review" ||
            r.status === "in_progress" ||
            r.status === "todo"
              ? r.status
              : "todo") as "todo" | "in_progress" | "review" | "done"
          }
          withDot
        />
      ),
    },
    {
      key: "gate",
      header: "Gate",
      width: "w-28",
      render: (r) =>
        r.strandedDone ? (
          <StatusBadge domain="verdict" status="pending" label="Stranded" withDot />
        ) : (
          <StatusBadge domain="task" status="review" label="Gated" withDot />
        ),
    },
    {
      key: "severity",
      header: "Severity",
      width: "w-28",
      render: (r) => (
        <span className="type-subtext text-muted-foreground">
          {r.severity ?? r.kind ?? "—"}
        </span>
      ),
    },
  ];

  if (error) {
    return (
      <Card className="p-6 type-body text-muted-foreground">
        Failed to load override queue: {(error as Error).message}
      </Card>
    );
  }

  if (isLoading && items.length === 0) {
    return (
      <Card className="p-6 type-body text-muted-foreground">
        Loading override queue…
      </Card>
    );
  }

  if (items.length === 0) {
    return (
      <Card className="p-6 type-body text-muted-foreground">
        No human-gated or abstain-routed items awaiting a decision. When a
        consequential abstention escalates, it lands here as an override-as-diff
        row (presented_as=diff, wall_ms measured).
      </Card>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      {msg ? (
        <p className="type-subtext text-muted-foreground" role="status">
          {msg}
        </p>
      ) : null}
      <DataTable
        rows={items}
        columns={columns}
        rowKey={(r) => r.taskId}
        onRowClick={(r) => setSelectedId(r.taskId)}
        rowClassName={(r) =>
          selected?.taskId === r.taskId ? "bg-muted/50" : undefined
        }
        wrapInCard
      />
      {selected ? (
        <Card className="gap-0 overflow-hidden py-0">
          <CardContent className="p-4">
            <OverrideRowDecision
              item={selected}
              presentedAtMs={presentedAtMs}
              busy={busyId === selected.taskId}
              onBusy={(v) => setBusyId(v ? selected.taskId : null)}
              onDone={() => {
                setMsg(`Recorded override.human for #${selected.taskId}`);
                refresh();
                setSelectedId(null);
              }}
            />
          </CardContent>
        </Card>
      ) : null}
    </div>
  );
}

function fieldPreview(value: unknown): string {
  if (value == null) return "—";
  if (typeof value === "string") return value.length > 0 ? value : "—";
  if (typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }
  try {
    return JSON.stringify(value);
  } catch {
    return "—";
  }
}

function asRecord(v: unknown): Record<string, unknown> | null {
  if (v !== null && typeof v === "object" && !Array.isArray(v)) {
    return v as Record<string, unknown>;
  }
  return null;
}

function RecordedOverrideDiff({ item }: { item: OverrideRecordItem }) {
  const before = asRecord(item.before);
  const after = asRecord(item.after);
  const keys =
    item.changedKeys.length > 0
      ? item.changedKeys
      : [
          ...new Set([
            ...Object.keys(before ?? {}),
            ...Object.keys(after ?? {}),
          ]),
        ];
  if (keys.length === 0) {
    return (
      <p className="type-subtext text-muted-foreground">
        No field-level delta on this record.
      </p>
    );
  }
  return (
    <dl className="grid grid-cols-[minmax(5rem,auto)_1fr_1fr] gap-x-3 gap-y-1 type-subtext">
      <dt className="text-muted-foreground">field</dt>
      <dd className="text-muted-foreground">system proposed</dd>
      <dd className="text-muted-foreground">human changed</dd>
      {keys.map((key) => (
        <React.Fragment key={key}>
          <dt className="font-mono">{key}</dt>
          <dd className="font-mono break-all text-muted-foreground">
            {fieldPreview(before ? before[key] : item.before)}
          </dd>
          <dd className="font-mono break-all">
            {fieldPreview(after ? after[key] : item.after)}
          </dd>
        </React.Fragment>
      ))}
    </dl>
  );
}

function signatureBadge(status: OverrideRecordItem["signatureStatus"]) {
  if (status === "signed") {
    return (
      <StatusBadge domain="verdict" status="accepted" label="Signed" withDot />
    );
  }
  if (status === "invalid") {
    return (
      <StatusBadge domain="verdict" status="rejected" label="Invalid" withDot />
    );
  }
  return (
    <StatusBadge domain="verdict" status="pending" label="Unsigned" withDot />
  );
}

function pathBadge(path: string) {
  if (path === "accept") {
    return <StatusBadge domain="verdict" status="accepted" withDot />;
  }
  if (path === "reject") {
    return <StatusBadge domain="verdict" status="rejected" withDot />;
  }
  if (path === "edit") {
    return <StatusBadge domain="verdict" status="edited" withDot />;
  }
  return <StatusBadge domain="verdict" status="pending" label={path} withDot />;
}

/** Recorded OverrideRecords rendered as before/after diffs. */
export function RecordedOverrideDiffs() {
  const { data, error, isLoading } = useApi(overrides.records, {
    query: { limit: "50" },
  });
  const [selectedKey, setSelectedKey] = React.useState<string | null>(null);
  const items = data?.items ?? [];
  const selected =
    items.find((i) => `${i.sessionId}:${i.seq}` === selectedKey) ??
    items[0] ??
    null;

  const columns: DataTableColumn<OverrideRecordItem>[] = [
    {
      key: "subject",
      header: "Subject",
      render: (r) => (
        <div className="min-w-0">
          <div className="truncate font-medium">{r.subject ?? "—"}</div>
          <div className="truncate type-subtext text-muted-foreground">
            {r.actorId} · {formatRelativeTime(r.createdAt)}
          </div>
        </div>
      ),
    },
    {
      key: "path",
      header: "Path",
      width: "w-28",
      render: (r) => pathBadge(r.path),
    },
    {
      key: "signature",
      header: "Signature",
      width: "w-28",
      render: (r) => signatureBadge(r.signatureStatus),
    },
    {
      key: "changed",
      header: "Changed",
      render: (r) => (
        <span className="type-subtext text-muted-foreground">
          {r.changedKeys.length > 0 ? r.changedKeys.join(", ") : "—"}
        </span>
      ),
    },
  ];

  if (error) {
    return (
      <Card className="p-6 type-body text-muted-foreground">
        Failed to load recorded overrides: {(error as Error).message}
      </Card>
    );
  }

  if (isLoading && items.length === 0) {
    return (
      <Card className="p-6 type-body text-muted-foreground">
        Loading recorded overrides…
      </Card>
    );
  }

  if (items.length === 0) {
    return (
      <Card className="p-6 type-body text-muted-foreground">
        No recorded OverrideRecords yet. A decision on the queue above writes
        a signed before/after diff here.
      </Card>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <DataTable
        rows={items}
        columns={columns}
        rowKey={(r) => `${r.sessionId}:${r.seq}`}
        onRowClick={(r) => setSelectedKey(`${r.sessionId}:${r.seq}`)}
        rowClassName={(r) =>
          selected &&
          selected.sessionId === r.sessionId &&
          selected.seq === r.seq
            ? "bg-muted/50"
            : undefined
        }
        wrapInCard
      />
      {selected ? (
        <Card className="gap-0 overflow-hidden py-0">
          <CardContent className="space-y-3 p-4">
            <div className="flex flex-wrap items-center gap-2">
              {pathBadge(selected.path)}
              {signatureBadge(selected.signatureStatus)}
              {selected.presentedAs ? (
                <StatusBadge
                  domain="verdict"
                  status="edited"
                  label={selected.presentedAs}
                />
              ) : null}
            </div>
            {selected.reasonText ? (
              <p className="type-subtext text-muted-foreground">
                {selected.reasonText}
              </p>
            ) : null}
            <RecordedOverrideDiff item={selected} />
          </CardContent>
        </Card>
      ) : null}
    </div>
  );
}
